import React, { useRef, useState, useEffect } from "react";
import { resizeImage } from "../utils/resizeImage";
import {
  loadPrivateKey,
  exportPrivateKey,
  encryptPrivateKeyBackup,
} from "../utils/Cryptoutils";

export function Settings({ currentUser, myChatId, myDisplayName, uploadImage, socket, settingsSocket, onBack, rentRefundRequest,
  setRentRefundRequest, theme, onToggleTheme }) {
  const fileInputRef = useRef(null);
  const [view, setView] = useState("profile");

  const chatInfo = currentUser?.chatInfo?.find((c) => String(c.chatId) === String(myChatId));
  const backendUsername = chatInfo?.userName ?? chatInfo?.username ?? "";

  const [localPreview, setLocalPreview] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [username, setUsername] = useState("");
  const [optimisticUsername, setOptimisticUsername] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
   const [closingAd, setClosingAd] = useState(false);
  const [rentRefundError, setRentRefundError] = useState(null);
  const [rentRefundNotice, setRentRefundNotice] = useState(null);
  const displayUsername = optimisticUsername ?? backendUsername;

  useEffect(() => { setUsername(displayUsername.startsWith("@") ? displayUsername.slice(1) : displayUsername); }, [displayUsername]);
 useEffect(() => {
  setDisplayName(
    myDisplayName ||
    currentUser?.displayName ||
    backendUsername.replace(/^@+/, "")
  );
}, [
  myDisplayName,
  currentUser?.displayName,
  backendUsername,
]);
  useEffect(() => { setEmail(currentUser?.email || ""); }, [currentUser?.email]);
  useEffect(() => { setLocalPreview(null); }, [currentUser?.profileImageUrl]);
  useEffect(() => { if (optimisticUsername && backendUsername) setOptimisticUsername(null); }, [backendUsername]);
  useEffect(() => {
    if (view === "account") { setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setShowCurrent(false); setShowNew(false); setShowConfirm(false); }
  }, [view]);

useEffect(() => {
    if (!socket) return;

    const onCloseAdSuccess = () => {
      setClosingAd(false);
      setRentRefundError(null);
      setRentRefundRequest?.(null);
      setRentRefundNotice("Rent deposit refunded. You can now delete your account.");
    };

    const onCloseAdError = ({ message } = {}) => {
      setClosingAd(false);
      setRentRefundError(message || "Failed to close old ad accounts. Please try again.");
    };

    socket.on("usdt:close-ad:success", onCloseAdSuccess);
    socket.on("usdt:close-ad:error", onCloseAdError);

    return () => {
      socket.off("usdt:close-ad:success", onCloseAdSuccess);
      socket.off("usdt:close-ad:error", onCloseAdError);
    };
  }, [socket, setRentRefundRequest]);
  if (!currentUser) return null;
  const avatarSrc = localPreview || currentUser.profileImageUrl || null;

 const handleAvatarChange = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  const resized = await resizeImage(file, { maxSize: 512, quality: 0.8 });
  setLocalPreview(URL.createObjectURL(resized));

  try {
    const { url: imageUrl } = await uploadImage(resized, null, "avatar");

    if (!imageUrl) {
      throw new Error("Upload succeeded but no URL was returned.");
    }

    settingsSocket.updateAvatar(imageUrl);
  } catch (err) {
    // optional: surface a toast/error to the user here
    setLocalPreview(null); // revert optimistic preview since upload failed
  }
};

  const saveUsername = () => {
    const raw = username.trim();
    if (!raw) return;
    setOptimisticUsername(raw);
    settingsSocket.updateUsername(raw);
    setEditingName(false);
  };

  const cancelEdit = () => {
    setUsername(backendUsername.startsWith("@") ? backendUsername.slice(1) : backendUsername);
    setEditingName(false);
    setOptimisticUsername(null);
  };

  const saveAccount = async () => {

    if (!currentPassword) return;

    let encryptedPrivateKey = null;
    let salt = null;

    // Only re-encrypt if password changes
    if (newPassword) {

        //
        // ECDH KEY
        //

        const ecdhPrivateKey =
            await loadPrivateKey(myChatId);

        if (!ecdhPrivateKey) {
            alert("Missing E2EE private key.");
            return;
        }

        const ecdhJwk =
            await exportPrivateKey(
                ecdhPrivateKey
            );

        const ecdhBackup =
            await encryptPrivateKeyBackup(
                ecdhJwk,
                newPassword
            );

        encryptedPrivateKey =
            JSON.stringify({
                ciphertext:
                    ecdhBackup.encryptedPrivateKey,
                iv:
                    ecdhBackup.iv,
            });

        salt =
            ecdhBackup.salt;
    }

    settingsSocket.updateAccount({

        chatId: myChatId,

        currentPassword,

        newEmail: email,

        newPassword:
            newPassword || undefined,

        confirmPassword:
            newPassword
                ? confirmPassword
                : undefined,

        encryptedPrivateKey,

        salt,

    });

    setView("profile");
};

  const saveDisplayName = () => {
    const raw = displayName.trim();
    if (!raw) return;
    settingsSocket.updateDisplayName(raw);
    setEditingDisplayName(false);
  };

  const cancelDisplayNameEdit = () => {
  setDisplayName(
    myDisplayName ||
    currentUser?.displayName ||
    backendUsername.replace(/^@+/, "")
  );
  setEditingDisplayName(false);
};
const confirmDeleteAccount = () => {
    if (!deletePassword) return;
    settingsSocket.deleteAccount({ currentPassword: deletePassword });
    setDeleteOpen(false);
    setDeletePassword("");
  };

  const handleCloseAdAndRefundRent = async () => {
    if (!rentRefundRequest?.adId || !socket || closingAd) return;

    setClosingAd(true);
    setRentRefundError(null);
    setRentRefundNotice(null);

    // Fully custodial now — backend builds + signs both close-account
    // legs itself, so the client just needs to tell it which ad to close.
    socket.emit("usdt:close-ad", {
      adId: rentRefundRequest.adId,
    });
  };

  const inputCls = "w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-3 sm:py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all";
  return (
    <div className="relative flex-1 w-full bg-white dark:bg-neutral-900 flex flex-col" style={{ height: "100%", overflow: "hidden" }}>

      {/* HEADER */}
           {/* HEADER */}
      <div
        className="flex-shrink-0 flex items-center gap-2 px-2 py-3 border-b dark:border-neutral-800"
        style={{ paddingTop: "max(12px, env(safe-area-inset-top, 12px))" }}
      >
        <button
          onClick={() => (view === "account" ? setView("profile") : onBack())}
          className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h2 className="ml-1 text-base sm:text-lg font-semibold">{view === "account" ? "Account" : "Settings"}</h2>
        {view === "profile" && (
          <button
            onClick={onToggleTheme} aria-label="Toggle theme"
            className="ml-auto rounded-full border px-3 py-1.5 text-sm shadow bg-white text-neutral-800 border-neutral-300 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-600 touch-manipulation"
          >
            {theme === "light" ? "🌙" : "☀️"}
          </button>
        )}
      </div>

      {/* SCROLLABLE BODY */}
      <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", paddingBottom: "env(safe-area-inset-bottom, 16px)" }}>
{/* PROFILE VIEW */}
{view === "profile" && (
  <div className="flex flex-col items-center px-4 sm:px-6 py-8 sm:py-10 gap-6">

    {/* AVATAR */}
    <div className="flex flex-col items-center gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleAvatarChange}
        className="hidden"
      />

      <div
        onClick={() => setZoomOpen(true)}
        className="w-24 h-24 rounded-full bg-emerald-600 text-white text-4xl flex items-center justify-center overflow-hidden cursor-pointer touch-manipulation active:opacity-80 shadow-lg"
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            className="w-full h-full object-cover"
            alt="avatar"
          />
        ) : (
          "👤"
        )}
      </div>

      <button
        onClick={() => fileInputRef.current?.click()}
        className="px-4 py-2 sm:py-1.5 text-sm rounded-full bg-neutral-800 dark:bg-neutral-700 text-white touch-manipulation active:opacity-70"
      >
        Edit
      </button>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
        Tap Edit to change your profile picture.
      </p>
    </div>

    {/* USERNAME */}
    <div className="w-full max-w-sm">
      <div className="text-xs uppercase text-neutral-500 text-center mb-2 font-medium tracking-wide">
        Username
      </div>

      {!editingName ? (
        <>
          <div
            onClick={() => setEditingName(true)}
            className="text-center text-lg font-medium cursor-pointer hover:underline touch-manipulation py-1"
          >
            {displayUsername || "Set username"}
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center mt-1">
            Tap your username to change it.
          </p>
        </>
      ) : (
        <div>
          <input
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveUsername()}
            className={inputCls}
          />

          <div className="mt-3 flex gap-3">
            <button
              onClick={saveUsername}
              className="flex-1 rounded-xl bg-green-600 text-white py-3 sm:py-2 text-sm touch-manipulation hover:bg-green-700"
            >
              Save
            </button>

            <button
              onClick={cancelEdit}
              className="flex-1 rounded-xl bg-neutral-200 dark:bg-neutral-700 py-3 sm:py-2 text-sm touch-manipulation"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {/* DISPLAY NAME */}
    <div className="w-full max-w-sm">
      <div className="text-xs uppercase text-neutral-500 text-center mb-2 font-medium tracking-wide">
        Display Name
      </div>

      {!editingDisplayName ? (
        <>
          <div
            onClick={() => setEditingDisplayName(true)}
            className="text-center text-lg font-medium cursor-pointer hover:underline touch-manipulation py-1"
          >
            {displayName}
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center mt-1">
            Tap your display name to change it.
          </p>
        </>
      ) : (
        <div>
          <input
            autoFocus
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveDisplayName()}
            className={inputCls}
          />

          <div className="mt-3 flex gap-3">
            <button
              onClick={saveDisplayName}
              className="flex-1 rounded-xl bg-green-600 text-white py-3 sm:py-2 text-sm touch-manipulation hover:bg-green-700"
            >
              Save
            </button>

            <button
              onClick={cancelDisplayNameEdit}
              className="flex-1 rounded-xl bg-neutral-200 dark:bg-neutral-700 py-3 sm:py-2 text-sm touch-manipulation"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {/* ACCOUNT LINK */}
    <div className="text-center">
      <div className="text-xs uppercase text-neutral-500 mb-2 font-medium tracking-wide">
        Account
      </div>

      <button
        onClick={() => setView("account")}
        className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline touch-manipulation"
      >
        Change email & password
      </button>

      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
        Update your email address or change your password.
      </p>
    </div>

  </div>
)}

{/* ACCOUNT VIEW */}
{view === "account" && (
  <div
    key="account-view"
    className="px-4 sm:px-6 py-8 sm:py-10 max-w-sm mx-auto w-full"
  >
    <div className="text-xs uppercase text-neutral-500 mb-1 font-medium tracking-wide">
      Account Settings
    </div>

    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
      Update your account details and password.
    </p>

    <div className="flex flex-col gap-4">

      <div>
        <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400 mb-1.5">
          Email
        </label>

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className={inputCls}
        />
      </div>

      {[
        {
          label: "Current Password",
          value: currentPassword,
          onChange: setCurrentPassword,
          show: showCurrent,
          toggle: () => setShowCurrent(!showCurrent),
          autoComplete: "current-password",
          name: "current-password-field",
        },
        {
          label: "New Password",
          value: newPassword,
          onChange: setNewPassword,
          show: showNew,
          toggle: () => setShowNew(!showNew),
          autoComplete: "new-password",
          name: "new-password-field",
        },
        {
          label: "Confirm New Password",
          value: confirmPassword,
          onChange: setConfirmPassword,
          show: showConfirm,
          toggle: () => setShowConfirm(!showConfirm),
          autoComplete: "new-password",
          name: "confirm-password-field",
        },
      ].map(
        ({
          label,
          value,
          onChange,
          show,
          toggle,
          autoComplete,
          name,
        }) => (
          <div key={name}>
            <label className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400 mb-1.5">
              {label}
            </label>

            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                name={name}
                className={`${inputCls} pr-16`}
              />

              <button
                onClick={toggle}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500 hover:text-neutral-700 touch-manipulation px-1 py-1"
              >
                {show ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        )
      )}

      <button
        onClick={saveAccount}
        className="mt-2 rounded-xl bg-emerald-600 text-white py-3.5 sm:py-3 text-sm font-semibold touch-manipulation hover:bg-emerald-700"
      >
        Save changes
      </button>

      <div className="mt-4 border-t pt-4 dark:border-neutral-700">
        <button
          onClick={() => setDeleteOpen(true)}
          className="w-full rounded-xl border border-red-500 text-red-600 py-3.5 sm:py-3 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 touch-manipulation"
        >
          Delete account
        </button>

        <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center mt-2">
          Permanently delete your account and its data.
        </p>
      </div>

    </div>
  </div>
)}
      </div>

      {/* AVATAR ZOOM MODAL */}
      {zoomOpen && (
        <div onClick={() => setZoomOpen(false)} className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <div onClick={(e) => e.stopPropagation()} className="relative w-full h-full flex items-center justify-center">
            <button onClick={() => setZoomOpen(false)} className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center text-xl touch-manipulation" style={{ top: "max(16px, env(safe-area-inset-top, 16px))" }}>×</button>
            {avatarSrc
              ? <div className="w-full max-w-[420px] aspect-square"><img src={avatarSrc} alt="Profile preview" className="w-full h-full object-cover rounded-xl" /></div>
              : <div className="w-64 h-64 bg-emerald-600 flex items-center justify-center text-white text-8xl rounded-full">👤</div>
            }
          </div>
        </div>
      )}
{/* DELETE ACCOUNT MODAL */}
      {deleteOpen && (
        <div onClick={() => setDeleteOpen(false)} className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-white dark:bg-neutral-900 p-5 sm:p-6"
            style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom, 20px))" }}
          >
            <h3 className="text-lg font-semibold text-red-600 mb-2">Delete account</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              This action is <strong>permanent</strong>. Your profile, USWD balance, and USDT balance will be deleted.<br /><br />Enter your password to confirm.
            </p>
            <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="Current password" className={`${inputCls} mb-4`} />
            <div className="flex gap-3">
              <button onClick={() => setDeleteOpen(false)} className="flex-1 rounded-xl bg-neutral-200 dark:bg-neutral-700 py-3 sm:py-2 text-sm touch-manipulation">Cancel</button>
              <button onClick={confirmDeleteAccount} className="flex-1 rounded-xl bg-red-600 text-white py-3 sm:py-2 text-sm touch-manipulation hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
{/* RENT REFUND REQUIRED MODAL */}
      {rentRefundRequest && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center">
          <div
            className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-white dark:bg-neutral-900 p-5 sm:p-6"
            style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom, 20px))" }}
          >
            <h3 className="text-lg font-semibold text-amber-600 mb-2">Rent deposit must be refunded first</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              {rentRefundRequest.message ||
                "Your account has a refundable rent deposit that must be returned before it can be deleted."}
              {typeof rentRefundRequest.amount === "number" && (
                <><br /><br />Amount: <strong>{rentRefundRequest.amount} USWD</strong></>
              )}
            </p>

            {rentRefundError && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {rentRefundError}
              </div>
            )}

            {rentRefundNotice && (
              <div className="mb-4 rounded-lg border border-green-300 bg-green-50 dark:border-green-900/50 dark:bg-green-900/20 px-3 py-2 text-sm text-green-700 dark:text-green-300">
                {rentRefundNotice}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setRentRefundRequest?.(null);
                  setRentRefundError(null);
                  setRentRefundNotice(null);
                }}
                className="flex-1 rounded-xl bg-neutral-200 dark:bg-neutral-700 py-3 sm:py-2 text-sm touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseAdAndRefundRent}
                disabled={!rentRefundRequest.adId || closingAd}
                className="flex-1 rounded-xl bg-amber-600 text-white py-3 sm:py-2 text-sm touch-manipulation hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {closingAd ? "Refunding…" : "Refund rent"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}